import type { AdminMetricsWindow, AdminViewStatsDto } from '@carcheck/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';
import { useHasPermission } from '../auth';

/**
 * Dashboard platform view summary (`GET /admin/view-stats?window=<w>`,
 * AUT-977): totals, per-day series and the two top-10 lists in ONE fetch.
 * Server-side `view_analytics` only, so the query is disabled without it
 * (no 403 request from an admin who sees locked tiles).
 */

const VIEW_STATS_BASE = '/admin/view-stats';

export const adminViewStatsKey = (
  window: AdminMetricsWindow,
): readonly [string, string, AdminMetricsWindow] => ['admin', 'view-stats', window] as const;

/** Poll cadence for the dashboard (ms). */
export const VIEW_STATS_REFETCH_MS = 60_000;

export const useAdminViewStats = (
  window: AdminMetricsWindow,
): UseQueryResult<AdminViewStatsDto, Error> => {
  const enabled = useHasPermission('view_analytics');
  return useQuery({
    queryKey: adminViewStatsKey(window),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminViewStatsDto>(VIEW_STATS_BASE, {
        params: { window },
        signal,
      });
      return data;
    },
    enabled,
    refetchInterval: VIEW_STATS_REFETCH_MS,
  });
};
